/**
 * "A new version of Defera is available" — offered, never forced.
 *
 * A tab opened before a deploy keeps running the JavaScript it loaded (see
 * src/lib/buildVersion.js for why, and the incidents it came from). This
 * notices and offers a one-tap refresh.
 *
 * It never reloads by itself. An automatic reload in the middle of checkout,
 * a card update, or a half-typed chat message would destroy the thing the
 * customer came to do, which is a worse failure than running an older build
 * for a few more minutes.
 *
 * Placement: a compact pill just BELOW the fixed nav (68px desktop, 82px on a
 * phone), not at the bottom. The bottom is taken on the pages that matter
 * most — Support's chat composer, Stays, the Inquiries Desk — and the shared
 * sonner Toaster lives there too, so moving that would move every other toast
 * in the app.
 *
 * Dismissing hides it for THIS deploy only (keyed on the deployed commit in
 * sessionStorage). The next deploy offers again, so a dismissal can never
 * leave someone on an old build for good.
 */
import { useCallback, useEffect, useState } from 'react';
import { isNativeApp } from '@/native/bootstrap';
import { RefreshCw, X } from 'lucide-react';
import {
  BUILD_COMMIT,
  isNewerBuildAvailable,
  refreshDeployedCommit,
} from '@/lib/buildVersion';

/** How often to re-check while the tab stays in view. The main trigger is the
 *  tab coming BACK into view; this only covers someone who never looks away. */
const RECHECK_MS = 10 * 60 * 1000;
/** Floor between any two checks, whatever triggered them. */
const MIN_INTERVAL_MS = 60 * 1000;
const DISMISS_KEY = 'defera.updateBanner.dismissedFor';

function readDismissed() {
  try { return sessionStorage.getItem(DISMISS_KEY); } catch { return null; }
}
function writeDismissed(commit) {
  try { sessionStorage.setItem(DISMISS_KEY, commit); } catch { /* private mode: show it again, which is fine */ }
}

export default function UpdateAvailableBanner() {
  const [deployed, setDeployed] = useState(null);

  // Shared with the search forms (src/lib/buildVersion.js), which check
  // more eagerly; a recent answer from either is reused rather than refetched.
  const check = useCallback(async () => {
    const commit = await refreshDeployedCommit({ maxAgeMs: MIN_INTERVAL_MS });
    if (commit && isNewerBuildAvailable(BUILD_COMMIT, commit)) setDeployed(commit);
  }, []);

  useEffect(() => {
    // A local bundle never needs this; skip the network entirely. Neither
    // does the iOS app: its code ships inside the app, so a reload would
    // just load the same build again.
    if (BUILD_COMMIT === 'local' || isNativeApp) return undefined;
    check();
    const onVisible = () => { if (document.visibilityState === 'visible') check(); };
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onVisible);
    const timer = setInterval(() => {
      if (document.visibilityState === 'visible') check();
    }, RECHECK_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onVisible);
      clearInterval(timer);
    };
  }, [check]);

  if (!deployed || readDismissed() === deployed) return null;

  const dismiss = () => {
    writeDismissed(deployed);
    setDeployed(null);
  };

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed inset-x-0 z-[70] flex justify-center px-4 pointer-events-none"
      style={{ top: 'calc(env(safe-area-inset-top, 0px) + 92px)' }}
    >
      <div
        className="pointer-events-auto flex items-center gap-1.5 max-w-full rounded-full border border-white/[0.12] bg-zinc-950/90 backdrop-blur-xl pl-4 pr-1.5 py-1.5 shadow-[0_18px_50px_-20px_rgba(0,0,0,0.9)]"
      >
        {/* One line on a 375px phone. The two-line version ("A new version of
            Defera is available.") doubled the pill's height and sat squarely on
            the Flights / Stays / Explore toggle beneath the nav. */}
        <p className="text-white/90 text-[13.5px] font-normal leading-tight whitespace-nowrap">
          New version available
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="inline-flex items-center gap-1.5 shrink-0 h-8 px-3 rounded-full bg-white text-zinc-950 text-[13px] font-medium hover:bg-white/90 transition"
        >
          <RefreshCw className="w-3.5 h-3.5" strokeWidth={2} />
          Refresh
        </button>
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss"
          className="inline-flex items-center justify-center shrink-0 w-8 h-8 rounded-full text-white/50 hover:text-white hover:bg-white/[0.08] transition"
        >
          <X className="w-4 h-4" strokeWidth={1.75} />
        </button>
      </div>
    </div>
  );
}
